import React, { useState, useEffect, useRef } from 'react';
import { X, Copy, Check, Loader2, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { orderService } from '../../../services/OrderService';
import { OrderResponse } from '../../../services/OrderService';
import { useLanguage } from '../../../contexts/LanguageContext';
import { generateOrderId } from '../../../utils/common';
import { useOrder } from '../../../contexts/OrderContext';
import { ORDER_SERVICE_TYPES } from '../../../utils/orderConstants';

export interface SelectedFile {
  base64: string;
  mimeType: string;
  preview: string;
  name: string;
}


export interface OrderFormData {
  externalOrderId: string;
  vin: string;
  model: string;
  serviceType: string;
  buyer: string;
  vinData: any;
  language: string;
  selectedFiles?: SelectedFile[];
}


export interface OrderFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: OrderFormData, orderId?: number) => void;
  /** Existing order for editing mode */
  order?: OrderResponse | null;
  /** Loading state for submit button */
  isSubmitting?: boolean;
}

const OrderFormModal: React.FC<OrderFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  order = null,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const isEditMode = !!order;

  // Form state
  const [externalOrderId, setExternalOrderId] = useState('');
  const [vin, setVin] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [buyer, setBuyer] = useState('');
  const [copied, setCopied] = useState(false);
  const [isDecoding, setIsDecoding] = useState(false);
  const [decodeError, setDecodeError] = useState<string | null>(null);
  const [isVinVerified, setIsVinVerified] = useState(false);
  const [vinData, setVinData] = useState<any>(null);
  const [uploadedFile, setUploadedFile] = useState<SelectedFile | null>(null);
  const [catalogUsed, setCatalogUsed] = useState<string | null>(null);

  // Ref for file input
  const vinFileInputRef = useRef<HTMLInputElement>(null);

  // VIN validation regex
  const VIN_REGEX = /^\b[(A-H|J-N|P|R-Z|0-9)]{17}\b/;

  // Generate order ID on mount or reset form when order changes
  useEffect(() => {
    if (isOpen) {
      if (order) {
        // Edit mode - populate form with existing order data
        setExternalOrderId(order.external_order_id || '');
        setVin(order.vin || '');
        setServiceType(order.service_type || '');
        setBuyer(order.buyer || '');
        setModel(''); // Model is not stored, would need to decode VIN again
        setIsVinVerified(!!order.vin); // Consider existing VIN as verified in edit mode
        setUploadedFile(null);
        setCatalogUsed(null);
      } else {
        // Create mode - generate new order ID
        setExternalOrderId(generateOrderId());
        setVin('');
        setModel('');
        setServiceType('');
        setBuyer('');
        setIsVinVerified(false);
        setUploadedFile(null);
        setCatalogUsed(null);
      }
      setDecodeError(null);
    }
  }, [isOpen, order]);

  const handleCopyOrderId = async () => {
    try {
      await navigator.clipboard.writeText(externalOrderId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Map a backend VIN-identification error to a translated message.
  // The identify-vin endpoints return a structured detail
  // ({ code, brand, message }) for known cases; plain-string details are
  // shown as-is (legacy behavior).
  const vinErrorFromResponse = (err: any): string | null => {
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === 'object') {
      if (detail.code === 'catalog_not_configured') {
        return detail.brand
          ? t('orderForm.errors.catalogNotSupported', {
              brand: detail.brand,
              defaultValue:
                "This vehicle's brand ({{brand}}) isn't covered by any parts catalog configured for your account, so this VIN can't be looked up.",
            })
          : t('orderForm.errors.catalogNotSupportedGeneric', {
              defaultValue:
                "This vehicle's brand isn't covered by any parts catalog configured for your account, so this VIN can't be looked up.",
            });
      }
      if (detail.code === 'vin_not_found') {
        return t('orderForm.errors.vinNotInCatalog', {
          defaultValue: 'The parts catalog has no vehicle for this VIN. Check the VIN and try again.',
        });
      }
      return typeof detail.message === 'string' ? detail.message : null;
    }
    return typeof detail === 'string' ? detail : null;
  };

  const handleDecodeVin = async () => {
    if (!vin || vin.length < 11) {
      setDecodeError(t('orderForm.errors.invalidVin', { defaultValue: 'Please enter a valid VIN (at least 11 characters)' }));
      return;
    }

    // Validate VIN format for 17-character VINs
    if (vin.length === 17 && !VIN_REGEX.test(vin)) {
      setDecodeError(t('orders.setupModal.invalidVin', { defaultValue: 'Invalid VIN format' }));
      return;
    }

    setIsDecoding(true);
    setDecodeError(null);
    setIsVinVerified(false);

    try {
      // Call backend identify-vin endpoint
      const response = await orderService.identifyVin(vin);

      const { data, status, catalog_used } = response.data || {};

      if (data && status === 'success') {
        // Extract make, model, year from the response
        setVinData(data);
        setCatalogUsed(catalog_used || null);
        const make = data.datasetName;
        const modelName = data.modelName || '';
        const year = data.modelQualifierName || '';

        if (make || modelName || year) {
          setModel([make, modelName, year].filter(Boolean).join(', '));
          setIsVinVerified(true);
          setMake(make);
        } else {
          // If structured data not found, try to use the raw response
          setModel(JSON.stringify(vinData));
          setIsVinVerified(true);
          setMake('');
        }
      } else {
        setDecodeError(t('orderForm.errors.vinNotFound', { defaultValue: 'Could not decode VIN. Please try again.' }));
        setIsVinVerified(false);
        setCatalogUsed(null);
      }
    } catch (err: any) {
      console.error('VIN decode error:', err);
      setDecodeError(vinErrorFromResponse(err) || t('orderForm.errors.decodeError', { defaultValue: 'Failed to decode VIN. Please try again.' }));
      setIsVinVerified(false);
      setCatalogUsed(null);
    } finally {
      setIsDecoding(false);
    }
  };

  const handleVinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVin = e.target.value.toUpperCase();
    setVin(newVin);
    // Reset verification when VIN changes
    if (newVin !== vin) {
      setIsVinVerified(false);
      setModel('');
      setDecodeError(null);
      setUploadedFile(null);
      setMake('');
      setCatalogUsed(null);
    }
  };

  const handleVinFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setDecodeError(t('orderForm.errors.fileTooLarge', {
        defaultValue: 'File is too large. Maximum size is 10MB.'
      }));
      event.target.value = '';
      return;
    }

    // Validate minimum file size (at least 1KB to ensure it's not empty)
    if (file.size < 1024) {
      setDecodeError(t('orderForm.errors.fileEmpty', {
        defaultValue: 'File appears to be empty or too small.'
      }));
      event.target.value = '';
      return;
    }

    // Validate file type - accept text files and common document formats
    const validTypes = ['text/plain', 'application/pdf', 'text/csv', 'application/vnd.ms-excel'];
    const validExtensions = ['.txt', '.csv', '.log', '.pdf'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      setDecodeError(t('orderForm.errors.invalidFileType', {
        defaultValue: 'Please upload a text file (.txt, .csv, .log, .pdf)'
      }));
      event.target.value = '';
      return;
    }

    setIsDecoding(true);
    setDecodeError(null);
    setIsVinVerified(false);

    try {
      const response = await orderService.identifyVinFile(file);

      const { extracted_data, status, catalog_used } = response.data || {};

      if (status === 'success' && extracted_data) {
        // Set the extracted VIN
        setVin(extracted_data.vin);
        setVinData(extracted_data);
        setCatalogUsed(catalog_used || null);
        setBuyer(extracted_data.buyer_name || '');
        setServiceType(extracted_data.service_type || '');

        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          const base64Data = result.split(',')[1];

          // Create a preview for the document file
          const filePreview: SelectedFile = {
            base64: base64Data,
            mimeType: file.type || 'text/plain',
            preview: result, // For documents, we'll use a placeholder or the data itself
            name: file.name
          };

          setUploadedFile(filePreview)
        };
        reader.readAsDataURL(file);

        const make = extracted_data.datasetName || '';
        const modelName = extracted_data.modelName || '';
        const year = extracted_data.modelQualifierName || '';

        if (make || modelName || year) {
          setModel([make, modelName, year].filter(Boolean).join(', '));
          setIsVinVerified(true);
        } else {
          setDecodeError(t('orderForm.errors.vinNotFound', {
            defaultValue: 'Could not decode VIN. Please try again.'
          }));
          setIsVinVerified(false);
        }
      } else {
        setDecodeError(t('orderForm.errors.vinNotFoundInFile', {
          defaultValue: 'No valid VIN found in file. Please try again.'
        }));
        setIsVinVerified(false);
        setUploadedFile(null);
        setCatalogUsed(null);
      }
    } catch (err: any) {
      console.error('VIN file upload error:', err);

      // Check for specific error messages
      let errorMessage = t('orderForm.errors.fileUploadError', {
        defaultValue: 'Failed to extract VIN from file. Please try again.'
      });

      const structuredError = vinErrorFromResponse(err);
      if (structuredError) {
        errorMessage = structuredError;
      } else if (err?.response?.data?.message) {
        const backendError = err.response.data.message;
        if (backendError.includes('no pages')) {
          errorMessage = t('orderForm.errors.invalidPdf', {
            defaultValue: 'The PDF file is invalid or empty. Please check the file and try again.'
          });
        } else {
          errorMessage = backendError;
        }
      }

      setDecodeError(errorMessage);
      setIsVinVerified(false);
      setUploadedFile(null);
      setCatalogUsed(null);
    } finally {
      setIsDecoding(false);
      event.target.value = '';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {

    e.preventDefault();
    if (!isVinVerified) {
      setDecodeError(t('orderForm.errors.vinRequired', { defaultValue: 'Please verify the VIN before saving' }));
      return;
    }
    onSubmit({
      externalOrderId,
      vin,
      model,
      serviceType,
      buyer,
      vinData,
      language: currentLanguage,
      selectedFiles: uploadedFile ? [uploadedFile] : []
    }, order?.id);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-xl font-medium text-gray-900">
            {isEditMode
              ? t('orderForm.titleEdit', { defaultValue: 'Edit Order' })
              : t('orderForm.titleCreate', { defaultValue: 'New Order' })
            }
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-50 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
          {/* Order ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('orderForm.orderId', { defaultValue: 'Order ID' })}
            </label>
            <div className="relative">
              <input
                type="text"
                value={externalOrderId}
                readOnly
                className="w-full px-4 py-3 pr-12 text-sm bg-gray-50 border-0 rounded-xl text-gray-900 cursor-default focus:outline-none"
              />
              <button
                type="button"
                onClick={handleCopyOrderId}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-all"
                title={t('orderForm.copyOrderId', { defaultValue: 'Copy Order ID' })}
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Vehicle Definition Section */}
          <div className="space-y-4">
            {/* VIN */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('orderForm.vinLabel', { defaultValue: 'VIN / Registration #' })} <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={vin}
                    onChange={handleVinChange}
                    placeholder="Enter VIN"
                    disabled={isDecoding}
                    className={`flex-1 px-4 py-3 text-sm border-0 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${isVinVerified ? 'bg-green-50 ring-2 ring-green-500' : 'bg-gray-50'
                      } ${isDecoding ? 'opacity-50 cursor-not-allowed' : ''}`}
                    maxLength={17}
                  />
                  <button
                    type="button"
                    onClick={() => vinFileInputRef.current?.click()}
                    disabled={isDecoding || isEditMode}
                    className="px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all"
                    title={t('orderForm.uploadVinFile', { defaultValue: 'Upload file to extract VIN' })}
                  >
                    <Upload className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleDecodeVin}
                    disabled={isDecoding || !vin || isEditMode}
                    className="px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  >
                    {isDecoding && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t('orderForm.decode', { defaultValue: 'Decode' })}
                  </button>
                </div>

                {uploadedFile && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg">
                    <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-sm text-blue-700">
                      {t('orderForm.uploadedFile', { defaultValue: 'Uploaded:' })} {uploadedFile.name}
                    </span>
                  </div>
                )}

                {decodeError && (
                  <div className="px-4 py-2 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-600">{decodeError}</p>
                  </div>
                )}

                {isVinVerified && !decodeError && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm text-green-700">
                        {t('orderForm.vinVerified', { defaultValue: 'VIN verified successfully' })}
                      </span>
                    </div>
                    {catalogUsed && (
                      <div className="px-4 py-2 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-700">
                          {t('orderForm.catalogUsed', { defaultValue: 'Catalog used:' })} <span className="font-medium">{catalogUsed}</span>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Model */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('orderForm.modelLabel', { defaultValue: 'Model' })}
              </label>
              <input
                type="text"
                value={make}
                readOnly
                disabled
                placeholder={t('orderForm.modelPlaceholder', { defaultValue: 'e.g. "Kia' })}
                className="w-full px-4 py-3 text-sm bg-gray-50 border-0 rounded-xl text-gray-900 cursor-default focus:outline-none"
              />
              <p className="mt-2 text-xs text-gray-500">
                {t('orderForm.modelHint', { defaultValue: '(e.g. "Kia")' })}
              </p>
            </div>
          </div>

          {/* Service Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('orderForm.serviceType', { defaultValue: 'Service Type' })}
            </label>
            <select
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
              disabled={isDecoding}
              className={`w-full px-4 py-3 text-sm border-0 bg-gray-50 rounded-xl focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer transition-all ${isDecoding ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
              <option value="">{t('orderForm.selectServiceType', { defaultValue: 'Service Type' })}</option>
              {ORDER_SERVICE_TYPES.map((st) => (
                <option key={st.value} value={st.value}>
                  {t(st.labelKey, { defaultValue: st.defaultLabel })}
                </option>
              ))}
            </select>
          </div>

          {/* Customer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('orderForm.notes', { defaultValue: 'Notes' })}
            </label>
            <input
              type="text"
              value={buyer}
              onChange={(e) => setBuyer(e.target.value)}
              disabled={isDecoding}
              placeholder={t('orderForm.notesPlaceholder', { defaultValue: 'Notes' })}
              className={`w-full px-4 py-3 text-sm border-0 bg-gray-50 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all ${isDecoding ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isDecoding}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-all disabled:opacity-50"
            >
              {t('orderForm.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isDecoding || !isVinVerified}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-sm"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEditMode
                ? t('orderForm.saveChanges', { defaultValue: 'Save Changes' })
                : t('orderForm.create', { defaultValue: 'Save Changes' })
              }
            </button>
          </div>
        </form>

        {/* Hidden VIN file input */}
        <input
          ref={vinFileInputRef}
          type="file"
          accept=".txt,.csv,.log,.pdf,text/plain,application/pdf"
          onChange={handleVinFileUpload}
          className="hidden"
        />
      </div>
    </div>
  );
};

export default OrderFormModal;
